import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useMemo } from 'react';
import { z } from 'zod';

import { ApplicationContext } from '~/shared/context/context';
import { useApplicationContext } from '~/shared/context/use-application-context';
import type { GeneralFilter } from '~/shared/data-contract/filter';
import {
    searchTypeLiteralSchema,
    skillTypeLiteralSchema,
    stateTypeLiteralSchema,
} from '~/shared/data-contract/filter-response';
import type { FilterState, FilterWithFilterStates } from '~/shared/data-contract/filter-state';
import {
    createCustomFilterSchema,
    createdAtFilterStateSchema,
    createdAtLiteralSchema,
    createPreDefinedFilterSchemas,
    createSortFilterStateByOrder,
    customFilterKeySchema,
    customFilterStateSchema,
    getFilterValuesFromFilterValueString,
    providerFilterStateSchema,
    providerLiteralSchema,
    reduceFiltersToKeyValuePairs,
    setFilterStateValuesToFilter,
    skillFilterStateSchema,
    updatedAtFilterStateSchema,
    updatedAtLiteralSchema,
} from '~/shared/data-contract/filter-state';
import type { LearningOpportunityState } from '~/shared/data-contract/learning-opportunities';
import { learningOpportunityStateSchema } from '~/shared/data-contract/learning-opportunities';
import type { Pathname } from '~/shared/http-fetcher/http-fetcher';

import { convertFilterStateToFilters } from '../../app/common/filter-sidebar/utils';
import type { FilterInFilterResponseTransformed } from '../../data-contract/filter-response-transformed';
import { filterResponseTransformedSchema } from '../../data-contract/filter-response-transformed';

const allowedLearningOpportunityFilterInOrder = [
    skillFilterStateSchema,
    providerFilterStateSchema,
    updatedAtFilterStateSchema,
    createdAtFilterStateSchema,
] as const;

const filterOrder = allowedLearningOpportunityFilterInOrder.map(({ shape }) => shape.field.value);
const allowedLearningOpportunityFilterFieldNameSchema = z.union([
    skillTypeLiteralSchema,
    providerLiteralSchema,
    updatedAtLiteralSchema,
    createdAtLiteralSchema,
]);
const allowedLearningOpportunityAndCustomFilterFieldNameSchema =
    allowedLearningOpportunityFilterFieldNameSchema.or(customFilterKeySchema);
const allowedLearningOpportunityFilterSchema = z.union(allowedLearningOpportunityFilterInOrder);
const allowedLearningOpportunityAndCustomFilterSchema =
    allowedLearningOpportunityFilterSchema.or(customFilterStateSchema);

type AllowedLearningOpportunityFilter = z.infer<typeof allowedLearningOpportunityFilterSchema>;

type AllowedLearningOpportunityOrCustomFilter = z.infer<typeof allowedLearningOpportunityAndCustomFilterSchema>;

const sortFilters = createSortFilterStateByOrder({ filterOrder });

const isAllowedOrCustomFilter = (
    filter: FilterInFilterResponseTransformed,
): filter is AllowedLearningOpportunityOrCustomFilter =>
    allowedLearningOpportunityAndCustomFilterFieldNameSchema.safeParse(filter.field).success;

const isAllowedFilter = (filter: FilterInFilterResponseTransformed): filter is AllowedLearningOpportunityFilter =>
    allowedLearningOpportunityFilterFieldNameSchema.safeParse(filter.field).success;

const filterQueryParam = 'learningOpportunityFilter';
const stateQueryParam = stateTypeLiteralSchema.value;
const defaultLearningOpportunityState: LearningOpportunityState = 'all';

const stateQueryParamSchema = z
    .string()
    .nullable()
    .transform((value) => {
        if (value === null) {
            return { isValid: true, state: defaultLearningOpportunityState };
        }

        const result = learningOpportunityStateSchema.safeParse(value);

        return result.success
            ? { isValid: true, state: result.data }
            : { isValid: false, state: defaultLearningOpportunityState };
    });

type SaveLearningOpportunityState = (state: LearningOpportunityState) => void;

export const useLearningOpportunityStateFilter = (): readonly [
    LearningOpportunityState,
    SaveLearningOpportunityState,
] => {
    const { actions, applicationState } = useApplicationContext();
    const stateParamValue = applicationState.locationSearchParams.get(stateQueryParam);
    const { isValid, state } = useMemo(() => stateQueryParamSchema.parse(stateParamValue), [stateParamValue]);

    useEffect(() => {
        if (!isValid) {
            actions.deleteLocationSearchParams([stateQueryParam]);
        }
    }, [actions, isValid]);

    const saveState: SaveLearningOpportunityState = (nextState) => {
        if (nextState === defaultLearningOpportunityState) {
            actions.deleteLocationSearchParams([stateQueryParam]);

            return;
        }

        applicationState.locationSearchParams.set(stateQueryParam, nextState);
        actions.setLocationSearchParams(applicationState.locationSearchParams);
    };

    return [state, saveState];
};

export type UseLearningOpportunityFilterResult = {
    readonly filterPayload: GeneralFilter | undefined;
    readonly filters: readonly FilterWithFilterStates[];
    readonly hasActiveFilters: boolean;
    readonly resetFilterValues: () => void;
    readonly saveFilterValues: (updatedFilters: readonly FilterWithFilterStates[]) => void;
};

export const useLearningOpportunityFilter = (): UseQueryResult<UseLearningOpportunityFilterResult> => {
    const { actions, applicationState, translate, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language, locationSearchParams } = applicationState;
    const pathname: Pathname = `/learning-opportunities/filter`;

    const resetFilterValues = (): void => {
        applicationState.locationSearchParams.delete(filterQueryParam);
        actions.setLocationSearchParams(applicationState.locationSearchParams);
    };

    const saveFilterValues = (updatedFilters: readonly FilterWithFilterStates[]): void => {
        const filterKeyValuePairs = reduceFiltersToKeyValuePairs(updatedFilters);

        if (is.emptyObject(filterKeyValuePairs)) {
            resetFilterValues();
        } else {
            applicationState.locationSearchParams.set(filterQueryParam, JSON.stringify(filterKeyValuePairs));
            actions.setLocationSearchParams(applicationState.locationSearchParams);
        }
    };

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(filterResponseTransformedSchema, {
                pathname,
            });

            if (result.isOk) {
                const allowedLearningOpportunityFilterSchemas = createPreDefinedFilterSchemas({ translate });
                const customLearningOpportunityFilterSchema = createCustomFilterSchema({
                    attributeNamespace: 'learningOpportunity',
                    translate,
                });
                const filterStates = result.value
                    .filter(isAllowedOrCustomFilter)
                    .sort(sortFilters)
                    .map((filter) => {
                        if (isAllowedFilter(filter)) {
                            const filterResult =
                                allowedLearningOpportunityFilterSchemas[filter.field].safeParse(filter);

                            if (filterResult.success) {
                                return filterResult.data;
                            }
                        }

                        return customLearningOpportunityFilterSchema.parse(filter);
                    });

                return [
                    {
                        id: 'skills',
                        filterStates: filterStates.filter(({ field }) => field === skillTypeLiteralSchema.value),
                        subTitle: translate('pages.learning-opportunities.filters.skills-preferences.sub-title'),
                        title: translate('pages.learning-opportunities.filters.skills-preferences.title'),
                    },
                    {
                        id: 'workSpace',
                        filterStates: filterStates.filter(({ field }) => field !== skillTypeLiteralSchema.value),
                        subTitle: translate('pages.learning-opportunities.filters.workspace-preferences.sub-title'),
                        title: translate('pages.learning-opportunities.filters.workspace-preferences.title'),
                    },
                ];
            }
            throw result.error;
        },
        queryKey: [pathname, 'get', language.code],
        throwOnError: false,
        select(filters) {
            const filterValuesJsonString = locationSearchParams.get(filterQueryParam);
            const search = locationSearchParams.get(searchTypeLiteralSchema.value);
            const state = locationSearchParams.get(stateQueryParam);
            const filterStatesWithSearchAndState: readonly FilterState[] = [
                {
                    field: 'title',
                    label: searchTypeLiteralSchema.value,
                    type: searchTypeLiteralSchema.value,
                    value: is.string(search) ? [search] : undefined,
                },

                {
                    field: stateTypeLiteralSchema.value,
                    label: stateTypeLiteralSchema.value,
                    type: stateTypeLiteralSchema.value,
                    value: is.string(state) && state !== 'all' ? [state] : undefined,
                },
            ];

            if (is.string(filterValuesJsonString)) {
                const filterValuesFromStorage = getFilterValuesFromFilterValueString({
                    filters,
                    filterValuesJsonString,
                });

                if (filterValuesFromStorage.isOk) {
                    const filtersWithValues = setFilterStateValuesToFilter(filters, filterValuesFromStorage.value);
                    const allFilterStatesWithValues = filtersWithValues.reduce(
                        // biome-ignore lint/performance/noAccumulatingSpread: suppressed due to migration
                        (acc, { filterStates }) => [...acc, ...filterStates],
                        [],
                    );

                    return {
                        filterPayload: convertFilterStateToFilters([
                            ...allFilterStatesWithValues,
                            ...filterStatesWithSearchAndState,
                        ]),
                        filters: filtersWithValues,
                        hasActiveFilters: allFilterStatesWithValues.some(({ value }) => !is.nullOrUndefined(value)),
                        resetFilterValues,
                        saveFilterValues,
                    };
                }
            }

            // biome-ignore lint/performance/noAccumulatingSpread: suppressed due to migration
            const allFilterStates = filters.reduce((acc, { filterStates }) => [...acc, ...filterStates], []);

            return {
                filterPayload: convertFilterStateToFilters([...allFilterStates, ...filterStatesWithSearchAndState]),
                filters,
                hasActiveFilters: allFilterStates.some(({ value }) => !is.nullOrUndefined(value)),
                resetFilterValues,
                saveFilterValues,
            };
        },
    });
};
